import { buildSearchCriteriaParams, type SearchCriteria } from '@/entities/searchCriteria/@/product';
import type { ProductSearchBaseParams, ProductSearchRoomParams } from '@/shared/api/productSearch';
import { toTouristGroupParams } from '@/shared/lib/touristGroupParams';
import { SEARCH_PRICE_RANGE } from '../config/search';
import type { ProductSearchRequest, ProductSearchRequestContext } from '../model/types';

function toRange<T extends string | number>(range: { from?: T; to?: T }): { from?: T; to?: T } | undefined {
  if (!range.from && !range.to) return undefined;

  return { ...(range.from && { from: range.from }), ...(range.to && { to: range.to }) };
}

export function buildProductSearchRequest(
  criteria: SearchCriteria,
  context: ProductSearchRequestContext,
): ProductSearchRequest {
  const params = buildSearchCriteriaParams(criteria);
  const { touristsByRooms = [], ...firstRoom } = params.touristGroup;
  const touristGroup = toTouristGroupParams(firstRoom);
  const rooms: ProductSearchRoomParams[] | undefined = touristsByRooms.length
    ? [touristGroup, ...touristsByRooms.map((room) => toTouristGroupParams(room))].map((group) => ({
        touristGroup: group,
      }))
    : undefined;
  const checkInDateRange = toRange(params.checkInDateRange);
  const nightRange = toRange(params.nightRange);
  const hotels = params.hotels?.length ? params.hotels : undefined;
  const resorts = params.resorts?.length ? params.resorts : undefined;
  const { meals, hotelCategories } = params;

  const base: ProductSearchBaseParams = {
    priceRange: { ...SEARCH_PRICE_RANGE },
    ...(params.departureCity.id && { departureCity: params.departureCity.id }),
    country: params.countries[0] ?? 0,
    ...(checkInDateRange && { checkInDateRange }),
    ...(nightRange && { nightRange }),
    touristGroup,
    ...(rooms && { rooms }),
    ...(hotels && { hotels }),
    ...(meals && { meals }),
    ...(criteria.directFlight && { transport: { flights: { direct: true } } }),
    ...(criteria.block && { block: true }),
    ...context.tracking,
    ...context.params,
  };

  return {
    cache: { ...base, ...(resorts && { resorts }), ...(hotelCategories && { hotelCategories }) },
    session: {
      ...base,
      ...(!hotels && resorts && { resorts }),
      ...(!hotels && hotelCategories && { hotelCategories }),
    },
    grouped: rooms !== undefined,
  };
}
